// ─── Broker Field Configuration ────────────────────────────────────────────────
// Each broker defines its credential fields (sent inside credentials: {}).
// totp_key is rendered as a top-level field. broker_client_id is top-level by
// default, but a broker can hide it when its dynamic fields already carry the
// canonical account identity (Kotak UCC).
//
// To add a new broker: add an entry to BROKER_CONFIGS.
// To change broker fields: edit the fields array for that broker.

export interface BrokerFieldDef {
  key:         string;
  label:       string;
  type:        'text' | 'password';
  placeholder?: string;
  requiredOnMap?: boolean;
}

export interface BrokerConfig {
  label:                string;
  accountIdentityLabel: string;
  accountIdentityField?: string;
  showBrokerClientId?:  boolean;
  // When true, the top-level broker_client_id field must be filled before a
  // new mapping can be submitted (e.g. Angel One mandatory Client ID).
  requireBrokerClientIdOnMap?: boolean;
  fields:               BrokerFieldDef[];
}

export const BROKER_CONFIGS: Record<string, BrokerConfig> = {
  fyers: {
    label: 'Fyers',
    accountIdentityLabel: 'FYERS Login ID',
    accountIdentityField: 'fy_id',
    showBrokerClientId: false,
    fields: [
      { key: 'fy_id',      label: 'FYERS Login ID', type: 'text',     placeholder: 'e.g. XA12345', requiredOnMap: false },
      { key: 'app_id',     label: 'App ID',         type: 'text',     placeholder: 'e.g. Y9PJY5D9W3-100', requiredOnMap: true },
      { key: 'secret_key', label: 'Secret Key',     type: 'password', placeholder: 'Fyers app secret key', requiredOnMap: true },
      { key: 'pin',        label: 'PIN',            type: 'password', placeholder: '4-digit FYERS PIN', requiredOnMap: false },
    ],
  },
  zerodha: {
    label: 'Zerodha',
    accountIdentityLabel: 'User ID',
    fields: [
      { key: 'api_key',    label: 'API Key',    type: 'password', placeholder: 'Kite API key' },
      { key: 'api_secret', label: 'API Secret', type: 'password', placeholder: 'Kite API secret' },
    ],
  },
  nuvama: {
    label: 'Nuvama',
    accountIdentityLabel: 'Login ID',
    fields: [
      { key: 'api_key',    label: 'API Key',    type: 'text',     placeholder: 'Nuvama API key' },
      { key: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'Nuvama secret key' },
    ],
  },
  angelone: {
    label: 'Angel One',
    accountIdentityLabel: 'Client ID',
    // The Angel One Client ID is mandatory: every newly generated login token
    // is verified against it before AlgoSys saves it.
    requireBrokerClientIdOnMap: true,
    fields: [
      { key: 'api_key',     label: 'API Key',     type: 'password', placeholder: 'Angel One API key' },
    ],
  },
  mastertrust: {
    label: 'MasterTrust',
    accountIdentityLabel: 'Client ID',
    fields: [
      { key: 'app_id', label: 'App ID', type: 'text', placeholder: 'MasterTrust OAuth app ID' },
      { key: 'secret_key', label: 'App Secret', type: 'password', placeholder: 'MasterTrust OAuth app secret' },
    ],
  },
  kotakneo: {
    label: 'Kotak Neo',
    accountIdentityLabel: 'UCC',
    showBrokerClientId: false,
    fields: [
      { key: 'consumer_key',  label: 'Consumer Key',  type: 'text',     placeholder: 'Kotak Neo consumer key' },
      { key: 'mobile_number', label: 'Mobile Number', type: 'text',     placeholder: 'Registered mobile number' },
      { key: 'ucc',           label: 'UCC',           type: 'text',     placeholder: 'Unique Client Code' },
      { key: 'mpin',          label: 'MPIN',          type: 'password', placeholder: 'Kotak Neo MPIN' },
    ],
  },
  aliceblue: {
    label: 'Alice Blue',
    accountIdentityLabel: 'Alice Blue User ID',
    fields: [
      { key: 'app_code', label: 'App Code', type: 'text', placeholder: 'Alice Blue Developer Portal app code' },
      { key: 'api_secret', label: 'API Secret', type: 'password', placeholder: 'Alice Blue API secret' },
    ],
  },
};

// Ordered list of broker names for select dropdowns
export const BROKER_NAMES = Object.keys(BROKER_CONFIGS);

// Returns the credential field definitions for a broker (empty array if unknown)
export function getBrokerFields(brokerName: string): BrokerFieldDef[] {
  return BROKER_CONFIGS[brokerName]?.fields ?? [];
}

export function shouldRenderBrokerClientId(brokerName: string): boolean {
  return BROKER_CONFIGS[brokerName]?.showBrokerClientId !== false;
}

// Whether the top-level broker_client_id must be provided when mapping.
export function isBrokerClientIdRequiredOnMap(brokerName: string): boolean {
  return BROKER_CONFIGS[brokerName]?.requireBrokerClientIdOnMap === true;
}
